import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CaseNotesService, Note } from 'src/app/services/case-notes.service';

export interface ShareNoteDialogData {
    note: Note;
    /** Name of the client this case was shared by — shown in the confirmation text. */
    clientName?: string;
}

type ShareState = 'idle' | 'submitting' | 'success' | 'error';

/**
 * Note-level sharing only — this is NOT "Share with Team". It shares a
 * single note with the client the case belongs to; there's no recipient
 * to type, just a confirmation.
 *
 * The backend "share note with client" endpoint isn't available yet, so
 * submission goes through CaseNotesService.shareNote(), which is a mock
 * with simulated latency. It currently accepts a free-text recipient
 * argument — we pass clientName through that same argument for now so
 * this stays a single, isolated call site to swap out once the real API
 * contract (likely case/relationship-scoped, no recipient string) lands.
 */
@Component({
    selector: 'app-share-note-dialog',
    templateUrl: './share-note-dialog.component.html',
    styleUrls: ['./share-note-dialog.component.scss'],
    standalone: false
})
export class ShareNoteDialogComponent {

    note: Note;
    clientName: string;
    state: ShareState = 'idle';
    errorMessage: string = '';

    constructor(
        public dialogRef: MatDialogRef<ShareNoteDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: ShareNoteDialogData,
        private caseNotesService: CaseNotesService
    ) {
        this.note = data.note;
        this.clientName = data.clientName || 'the client';
    }

    share(): void {
        if (this.state === 'submitting' || this.state === 'success') {
            return; // prevent duplicate submission
        }

        this.state = 'submitting';
        this.caseNotesService.shareNote(this.note.caseId, this.note.id, this.clientName).subscribe({
            next: () => {
                this.state = 'success';
            },
            error: () => {
                this.state = 'error';
                this.errorMessage = 'Something went wrong while sharing this note. Please try again.';
            }
        });
    }

    close(): void {
        if (this.state === 'submitting') {
            return; // don't let an in-flight share get silently abandoned
        }
        this.dialogRef.close(this.state === 'success');
    }
}